import { useAuth } from '@/lib/auth-store';

export interface ApiError {
  status: number;
  code: string;
  message: string;
  details?: unknown;
}

let refreshInFlight: Promise<string | null> | null = null;

async function tryRefresh(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const res = await fetch('/auth/refresh', { method: 'POST', credentials: 'include' });
    if (!res.ok) return null;
    const body = (await res.json()) as { access_token?: unknown };
    if (typeof body.access_token !== 'string') return null;
    useAuth.setState({ accessToken: body.access_token });
    return body.access_token;
  })();
  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

export async function apiFetch<T>(input: string, init: RequestInit = {}): Promise<T> {
  const { accessToken } = useAuth.getState();
  const headers = new Headers(init.headers);
  if (accessToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }
  const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData;
  if (init.body && !isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  let res = await fetch(input, { ...init, headers, credentials: 'include' });

  if (res.status === 401 && accessToken) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      headers.set('Authorization', `Bearer ${refreshed}`);
      res = await fetch(input, { ...init, headers, credentials: 'include' });
    } else {
      useAuth.getState().clear();
      if (window.location.pathname !== '/login') window.location.assign('/login');
      throw apiError(401, 'unauthenticated', 'Session expired.');
    }
  }

  if (!res.ok) {
    let body: {
      error?: string;
      detail?: string;
      message?: string;
      details?: unknown;
    } = {};
    try { body = await res.json(); } catch { /* ignore */ }
    const code = body.error ?? 'error';
    const message =
      body.detail
      ?? body.message
      ?? humanizeError(code, body.details)
      ?? res.statusText;
    throw apiError(res.status, code, message, body);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function apiError(status: number, code: string, message: string, details?: unknown): ApiError {
  const e = new Error(message) as Error & ApiError;
  e.status = status; e.code = code; e.message = message; e.details = details;
  return e;
}

// Friendly mapping for common backend error codes + zod flattened details.
// Keeps dialogs from showing generic "Bad Request" when the backend
// already told us which field is wrong.
function humanizeError(code: string, details: unknown): string | null {
  if (code === 'invalid_body') {
    const fields = extractZodFieldErrors(details);
    if (fields.length > 0) {
      return `Invalid input: ${fields.join('; ')}.`;
    }
    return 'The form has invalid fields. Please review and try again.';
  }
  switch (code) {
    case 'forbidden': return 'You do not have permission to do that.';
    case 'unauthenticated': return 'Session expired. Please sign in again.';
    case 'over_limit': return 'Limit reached for this account. Upgrade or free space to continue.';
    case 'customer_id_required':
    case 'missing_customer_scope': return 'Please pick a customer first (Customers → Enter).';
    case 'missing_reseller_scope': return 'Your account is missing a reseller scope — contact your administrator.';
    case 'resellerId_required': return 'Reseller ID is required.';
    case 'duplicate': return 'A record with those details already exists.';
    case 'conflict': return 'This conflicts with an existing record.';
    case 'not_found': return 'The requested item was not found.';
    case 'must_change_password': return 'You must change your password before continuing.';
    case 'recording_too_long': return "Recordings can't be longer than 1 hour.";
    case 'bulletin_limit_exceeded': return 'This bulletin is full (max 1,000 messages). Remove one before adding another.';
    case 'email_already_exists_in_scope': return 'An account with this email already exists. Ask your admin to reset your password instead of using an invite link.';
    default: return null;
  }
}

function extractZodFieldErrors(details: unknown): string[] {
  if (!details || typeof details !== 'object') return [];
  const d = details as { fieldErrors?: Record<string, string[] | undefined>; formErrors?: string[] };
  const out: string[] = [];
  if (d.fieldErrors) {
    for (const [field, msgs] of Object.entries(d.fieldErrors)) {
      if (msgs && msgs.length > 0) {
        out.push(`${field} — ${msgs.join(', ')}`);
      }
    }
  }
  if (d.formErrors && d.formErrors.length > 0) {
    out.push(...d.formErrors);
  }
  return out;
}
